import * as Y from 'yjs';
import { applyOps, defaultEffect, keysBetween, newId, NodeSchema, nodeToYMap, pageNodes, ROOT_ID, Scene, type Paint } from '@flobi/artwork-core';
import type { SelectionActions } from '../editor/shell/SelectionInspector';
import type { Rail, RailButton } from '../editor/tools/types';

/**
 * A small real document for the labs: one artboard, a card, a sun and a line of type,
 * built the way the stress scene is — parsed through the schema, so every record carries
 * the defaults the editor's own panels expect — and held in a real Scene, so the layers
 * list and the inspectors read it exactly as they read a file.
 */

export interface LabDocument {
  readonly page: Y.Doc;
  readonly scene: Scene;
  readonly board: string;
  readonly card: string;
  readonly sun: string;
  readonly title: string;
}

const solid = (r: number, g: number, b: number): Paint => ({ type: 'solid', color: { r, g, b, a: 1 }, visible: true, opacity: 1, blendMode: 'normal' });

export function buildLabDocument(): LabDocument {
  const page = new Y.Doc();
  const nodes = pageNodes(page);
  const board = newId();
  const card = newId();
  const sun = newId();
  const title = newId();
  const [boardOrder] = keysBetween(null, null, 1);
  const orders = keysBetween(null, null, 3);
  const put = (id: string, input: Record<string, unknown>) => nodes.set(id, nodeToYMap(NodeSchema.parse(input)));
  page.transact(() => {
    put(board, { type: 'artboard', parentId: ROOT_ID, order: boardOrder, name: 'Post', width: 1080, height: 1080, fills: [solid(1, 1, 1)], transform: [1, 0, 0, 1, 0, 0] });
    put(card, { type: 'rect', parentId: board, order: orders[0], name: 'Card', width: 520, height: 400, radii: [56, 56, 56, 56], fills: [solid(0.37, 0.45, 0.64)], transform: [1, 0, 0, 1, 130, 150] });
    put(sun, { type: 'ellipse', parentId: board, order: orders[1], name: 'Sun', width: 220, height: 220, fills: [solid(0.96, 0.72, 0.28)], transform: [1, 0, 0, 1, 740, 120] });
    put(title, {
      type: 'text',
      parentId: board,
      order: orders[2],
      name: 'Title',
      runs: [{ text: 'Hello Artwork' }],
      style: { fontSize: 88, fontWeight: 700 },
      transform: [1, 0, 0, 1, 120, 690],
    });
  });
  return { page, scene: new Scene(page), board, card, sun, title };
}

/** What the inspectors ask the canvas to do, done to the lab's own page. */
export function labActions(doc: LabDocument, selection: readonly string[] = [doc.title]): SelectionActions {
  const noop = () => {};
  return {
    sizeBoards: () => {},
    middleOf: () => null,
    boxInParent: () => null,
  editGradient: () => {},
  tidyBoards: () => {},
  exportBoards: () => {},
  update: (id, props) => void applyOps(doc.page, [{ op: 'set', id, props }], { scene: doc.scene }),
  updateAll: (ids, props) => void applyOps(doc.page, ids.map((id) => ({ op: 'set', id, props })), { scene: doc.scene }),
    restyle: noop,
    align: noop,
    tidyUp: noop,
    combine: noop,
    canCombine: false, canMask: false, mask: noop, maskOf: () => null, canReleaseMask: () => false,
    group: noop,
    ungroup: noop,
    expandBlend: () => {},
    releaseBlend: () => {},
    rasterize: () => {},
    outlineText: () => {},
    arrange: noop,
    flip: noop,
    addEffect: type => {
      for (const id of selection) { const node = doc.scene.get(id); if (node && 'effects' in node) applyOps(doc.page, [{ op: 'set', id, props: { effects: [...node.effects, defaultEffect(type)] } }], { scene: doc.scene }); }
    },
    setEffect: (ids, index, props) => {
      for (const id of typeof ids === 'string' ? [ids] : ids) { const node = doc.scene.get(id); if (node && 'effects' in node) applyOps(doc.page, [{ op: 'set', id, props: { effects: node.effects.map((effect, at) => at === index ? { ...effect, ...props } : effect) } }], { scene: doc.scene }); }
    },
    removeEffect: (ids, index) => {
      for (const id of typeof ids === 'string' ? [ids] : ids) { const node = doc.scene.get(id); if (node && 'effects' in node) applyOps(doc.page, [{ op: 'set', id, props: { effects: node.effects.filter((_, at) => at !== index) } }], { scene: doc.scene }); }
    },
    moveEffect: noop,
    setFont: noop,
    applyShader: async () => { throw Error('Shaders are available in an open Artwork file.'); },
    placeAnchor: noop,
    replaceImage: noop,
    fitImage: noop,
  };
}

// ── the rails the labs show, as the real tools declare them ────────────────

const button = (id: string, icon: RailButton['icon'], title: string, extra: Partial<RailButton> = {}): RailButton => ({ id, icon, title, run: () => {}, ...extra });

export function drawRail(mode: string, onMode: (mode: string) => void): Rail {
  const modes: [string, RailButton['icon'], string][] = [
    ['shape', 'magic', 'Shape assist — draw roughly and hold, and it reads what you meant'],
    ['pencil', 'draw', 'Pencil — keep exactly what you drew'],
    ['blob', 'brush', 'Fill brush — paint a filled shape, merging with what it touches'],
    ['smooth', 'smooth', 'Smooth — brush along a line to soften it'],
    ['erase', 'eraser', 'Erase — brush along a line to rub it out'],
    ['join', 'join', 'Join — press between two loose ends to close the gap'],
  ];
  return {
    top: { kind: 'buttons', items: modes.map(([id, icon, title]) => button(id, icon, title, { pressed: mode === id, run: () => onMode(id) })) },
    middle: { kind: 'slider', icon: 'draw', title: 'Smoothing — how much a wobbly hand is steadied', value: 40, min: 0, max: 100, onChange: () => {} },
    style: true,
  };
}

export const SHAPE_RAIL: Rail = {
  top: {
    kind: 'buttons',
    items: [
      button('rect', 'square', 'Rectangle', { pressed: true }),
      button('ellipse', 'circle', 'Ellipse', { pressed: false }),
      button('polygon', 'polygon', 'Polygon', { pressed: false }),
      button('star', 'star', 'Star', { pressed: false }),
      button('line', 'line', 'Line', { pressed: false }),
    ],
  },
  style: true,
};
